import { useState, useEffect } from 'react';
import { useApi } from '@/hooks/useApi';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Loader2, Plus, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import { useI18n } from '@/contexts/I18nContext';
import { DriverPayItemType } from '@/types/financial.types';

interface AddPayAdjustmentModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  settlementId: string;
  onSuccess: () => void;
}

export function AddPayAdjustmentModal({
  open,
  onOpenChange,
  settlementId,
  onSuccess,
}: AddPayAdjustmentModalProps) {
  const { t } = useI18n();
  const { post, loading } = useApi({ showSuccessToast: false, showErrorToast: false });
  const [type, setType] = useState<Exclude<DriverPayItemType, 'accrual'>>('bonus');
  const [amount, setAmount] = useState<string>('');
  const [description, setDescription] = useState<string>('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setType('bonus');
      setAmount('');
      setDescription('');
      setError(null);
    }
  }, [open]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    const parsed = parseFloat(amount);
    if (isNaN(parsed) || parsed <= 0) {
      setError('يرجى إدخال مبلغ صحيح');
      return;
    }

    try {
      await post(`/api/v1/driver-pay-settlements/${settlementId}/items`, {
        type,
        amount: parsed,
        description: description.trim() || undefined,
      });
      toast.success(t.financials.settlement_generated);
      onSuccess();
      onOpenChange(false);
    } catch (err: any) {
      const msg = err?.message ?? 'فشل في إضافة العنصر';
      setError(msg);
      toast.error(msg);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[460px]" dir="rtl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Plus className="h-4 w-4" />
            {t.financials.add_adjustment}
          </DialogTitle>
          <DialogDescription className="text-xs">
            إضافة عنصر (حافز / خصم / راتب / تسوية) إلى التسوية
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4 mt-2">
          <div className="space-y-2">
            <Label htmlFor="adj-type">
              {t.financials.adjustment_type} <span className="text-destructive">*</span>
            </Label>
            <Select value={type} onValueChange={(v) => setType(v as Exclude<DriverPayItemType, 'accrual'>)} disabled={loading}>
              <SelectTrigger id="adj-type">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="bonus">{t.financials.bonus}</SelectItem>
                <SelectItem value="penalty">{t.financials.penalty}</SelectItem>
                <SelectItem value="salary">{t.financials.salary}</SelectItem>
                <SelectItem value="adjustment">{t.financials.adjustment}</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="adj-amount">
              {t.financials.amount} <span className="text-destructive">*</span>
            </Label>
            <Input
              id="adj-amount"
              type="number"
              min={0}
              step="0.01"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              disabled={loading}
              dir="ltr"
              className="text-start"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="adj-desc">{t.financials.description}</Label>
            <Textarea
              id="adj-desc"
              placeholder="..."
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              disabled={loading}
              rows={3}
              className="resize-none"
            />
          </div>

          {error && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          <DialogFooter className="gap-2 pt-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={loading}
            >
              إلغاء
            </Button>
            <Button type="submit" disabled={loading} className="gap-2">
              {loading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Plus className="h-4 w-4" />
              )}
              {t.financials.add_adjustment}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
